import auth0 from "auth0-js";
import Cookies from "js-cookie";
import { toast } from "react-toastify";

// Auth0 configuration from environment variables with fallback values
const AUTH0_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN || "preprod.accounts.davensi.com";
const AUTH0_CLIENT_ID = import.meta.env.VITE_AUTH0_CLIENT_ID || "znbx1OLiPALggVaAr0SfEvSxTN7BnmJf";
const AUTH0_AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE || "https://lordtoken-dev.eu.auth0.com/api/v2/";
const AUTH0_CALLBACK_URL = import.meta.env.VITE_AUTH0_CALLBACK_URL || `${typeof window !== 'undefined' ? window.location.origin : 'https://pp-dev-webpprod.lovable.app'}/auth/callback`;

console.group("Auth0 Configuration");
console.log("Domain:", AUTH0_DOMAIN);
console.log("Client ID:", AUTH0_CLIENT_ID);
console.log("Audience:", AUTH0_AUDIENCE);
console.groupEnd();

// Only initialize Auth0 if credentials are configured
let authInstance: auth0.WebAuth | null = null;

const initAuth = () => {
  if (!authInstance && AUTH0_DOMAIN && AUTH0_CLIENT_ID && typeof window !== "undefined") {
    authInstance = new auth0.WebAuth({
      domain: AUTH0_DOMAIN,
      clientID: AUTH0_CLIENT_ID,
      redirectUri: AUTH0_CALLBACK_URL,
      audience: AUTH0_AUDIENCE,
      responseType: "token id_token",
      scope: "openid profile email offline_access",
    });
  }
  return authInstance;
};

export const auth = typeof window !== "undefined" ? initAuth() : null;

// Debug function to log Auth0 configuration
export const getAuth0Config = () => ({
  domain: AUTH0_DOMAIN,
  clientId: AUTH0_CLIENT_ID,
  audience: AUTH0_AUDIENCE,
  callbackUrl: AUTH0_CALLBACK_URL,
  isConfigured: !!auth,
});

export const getAccessToken = () => {
  return new Promise((resolve, reject) => {
    if (!auth) {
      reject(new Error("Auth0 is not configured"));
      return;
    }
    auth.checkSession({}, (err, authResult) => {
      if (err) {
        reject(err);
      } else {
        resolve(authResult.accessToken);
        Cookies.set("accessToken", authResult.accessToken, {
          secure: import.meta.env.PROD,
          sameSite: 'Lax',
        });
      }
    });
  });
};

export const logout = () => {
  if (!auth) {
    toast.error("Auth0 is not configured");
    return;
  }
  auth.logout({
    returnTo: window.location.origin,
  });
};

export const login = ({ email }: { email: string; }) => {
  return new Promise((resolve, reject) => {
    if (!auth) {
      toast.error("Auth0 n'est pas configuré correctement");
      reject(new Error("Auth0 not configured"));
      return;
    }

    // Switch from crossOriginAuthentication (embedded login) to redirect-based Universal Login
    console.group("🔐 AUTH0 LOGIN DEBUG");
    console.info("Login attempt with redirect (authorize)...");
    console.info("Domain:", AUTH0_DOMAIN);
    console.info("Client ID:", AUTH0_CLIENT_ID);
    console.info("Audience:", AUTH0_AUDIENCE);
    console.info("Callback URL:", AUTH0_CALLBACK_URL);
    console.info("Email (login_hint):", email);
    console.info("Current window.location.origin:", window.location.origin);
    console.groupEnd();

    try {
      const authorizeParams = {
        redirectUri: AUTH0_CALLBACK_URL,
        responseType: "token id_token",
        scope: "openid profile email offline_access",
        audience: AUTH0_AUDIENCE,
        connection: "Username-Password-Authentication",
        login_hint: email,
        prompt: "login",
      };

      console.log("📤 Authorize params:", authorizeParams);

      auth.authorize(authorizeParams);
      // authorize() performs a full-page redirect immediately and does not yield a callback.
      // Return a resolved promise to keep the original API contract.
      resolve(true);
    } catch (e) {
      console.error("❌ Login redirect failed to start:", e);
      reject(e);
    }
  });
};

export const signup = () => {
  if (!auth) {
    toast.error("Auth0 n'est pas configuré correctement");
    return Promise.reject(new Error("Auth0 not configured"));
  }

  console.info("Signup attempt starting with authorize...");
  console.info("Callback URL:", AUTH0_CALLBACK_URL);

  // Using authorize() with mode=signUp to show signup screen
  auth.authorize({
    connection: "Username-Password-Authentication",
    redirectUri: AUTH0_CALLBACK_URL,
    responseType: "token id_token",
    scope: "openid profile email offline_access",
    mode: "signUp",
  });

  // authorize() doesn't return a promise - it redirects immediately
  return Promise.resolve(true);
};

export const refreshAccessToken = (): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!auth) {
      reject(new Error("Auth0 is not configured"));
      return;
    }

    console.log("🔄 Attempting to refresh access token...");

    auth.checkSession({}, (err, authResult) => {
      if (err) {
        console.error("❌ Token refresh failed:", err);
        reject(err);
      } else if (authResult?.accessToken) {
        console.log("✅ Token refreshed successfully");
        Cookies.set("accessToken", authResult.accessToken, {
          secure: import.meta.env.PROD,
          sameSite: 'Lax',
        });
        resolve(authResult.accessToken);
      } else {
        reject(new Error("No access token in refresh result"));
      }
    });
  });
};

export const handleAuthCallback = () => {
  return new Promise((resolve, reject) => {
    if (!auth) {
      console.error("❌ Auth0 is not configured");
      reject(new Error("Auth0 is not configured"));
      return;
    }

    console.group("🔄 AUTH0 CALLBACK DEBUG");
    console.log("Current URL:", window.location.href);
    console.log("Hash:", window.location.hash);
    console.log("Search params:", window.location.search);
    console.groupEnd();

    auth.parseHash((err, authResult) => {
      if (err) {
        console.group("❌ AUTH0 CALLBACK ERROR");
        console.error("Error object:", err);
        console.error("Error code:", err.error);
        console.error("Error description:", err.error_description);
        console.error("Error details:", JSON.stringify(err, null, 2));
        console.groupEnd();

        // Check if this is an MFA challenge
        if (err.error === "mfa_required") {
          // Extract MFA token and email from URL hash
          const hash = window.location.hash;
          const params = new URLSearchParams(hash.substring(1));

          console.log("🔐 MFA Required");
          resolve({
            mfaRequired: true,
            mfaToken: params.get("state") || params.get("mfa_token"),
            email: params.get("email") || params.get("login_hint"),
          });
        } else {
          reject(err);
        }
      } else {
        console.group("✅ AUTH0 CALLBACK SUCCESS");
        console.log("Auth Result:", authResult);
        console.log("Access Token:", authResult?.accessToken ? "✓ Present" : "✗ Missing");
        console.log("ID Token:", authResult?.idToken ? "✓ Present" : "✗ Missing");
        console.log("ID Token Payload:", authResult?.idTokenPayload);
        console.groupEnd();

        resolve(authResult);
      }
    });
  });
};
